import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Box, Skeleton } from "@mui/material";
import { IconRefresh } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import FileViewer from "src/pages/request/FileViewer";
import VisaCard from "src/pages/request/VisaCard";
import { getRequest } from "src/pages/request/actions";

// The last step of a case, inside the case: this traveller's visa and its
// expiry. The visa lives on the traveller's request, so it is read from
// there. Adding or replacing it opens "Record the decision" at its visa step
// (onAdd), the one way a visa is sent.
export default function Visa({ traveller, travellerName, canEdit, onAdd }) {
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: true, failed: false, data: null });
  const [viewing, setViewing] = useState(null);

  const load = useCallback(
    (quiet) => {
      if (!quiet) setState((s) => ({ ...s, loading: true, failed: false }));
      return getRequest(traveller.request_id, dispatch)
        .then((response) => {
          if (!response?.data) throw new Error("missing");
          setState({ loading: false, failed: false, data: response.data });
        })
        .catch(() => setState((s) => ({ ...s, loading: false, failed: !s.data })));
    },
    [traveller.request_id, dispatch],
  );

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading && !state.data) return <Skeleton variant="rounded" height={96} />;
  if (!state.data) {
    return (
      <Alert
        severity="error"
        sx={{ alignItems: "center" }}
        action={
          <ActionButton startIcon={<IconRefresh size={16} />} onClick={() => load()}>
            Try again
          </ActionButton>
        }
      >
        The visa for this traveller did not load.
      </Alert>
    );
  }

  return (
    <Box>
      <VisaCard
        bare
        heading="h3"
        data={{ ...state.data, visa_sent_at: traveller.visa?.sent_at, status: traveller.status || state.data.status }}
        travellerName={travellerName}
        canEdit={canEdit}
        onAdd={onAdd}
        onView={setViewing}
      />
      {viewing && <FileViewer file={viewing.file} title={viewing.title} onClose={() => setViewing(null)} />}
    </Box>
  );
}

Visa.propTypes = {
  traveller: PropTypes.object.isRequired,
  travellerName: PropTypes.string,
  canEdit: PropTypes.bool,
  onAdd: PropTypes.func,
};
